// src/pages/CredentialsModal.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { credentialsAPI } from "../services/api.js";
import axios from "axios";
import "./CredentialsModal.css";

const API_BASE_URL = "https://quickpost.webzgrowth.in";

const CredentialsModal = ({ onClose }) => {
  const { user, token } = useAuth();

  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingPlatform, setConnectingPlatform] = useState(null);

  // ✅ S3 base host from .env (React env must start with REACT_APP_)
  const ICONS_BASE = (process.env.REACT_APP_S3_ICONS_BASE || "").replace(/\/$/, "");
  const IMAGES_BASE = (process.env.REACT_APP_S3_IMAGES_BASE || "").replace(/\/$/, "");

  const img = (fileName) => `${IMAGES_BASE}/${fileName}`;
  const ico = (fileName) => `${ICONS_BASE}/${fileName}`;

  useEffect(() => {
    if (user && token) fetchCredentials();
    // eslint-disable-next-line
  }, [user, token]);

  // lock body scroll while modal open (design only)
  useEffect(() => {
    document.body.classList.add("qs-modal-open");
    return () => document.body.classList.remove("qs-modal-open");
  }, []);

  const platforms = [
    {
      id: "facebook",
      name: "Facebook with Instagram",
      icon: ico("fb.svg"),
      secondIcon: ico("insta.svg"),
      type: "oauth_auto",
      fields: [],
      color: "#1877F2",
      displayName: "Instagram With Facebook",
    },
    {
      id: "youtube",
      name: "YouTube",
      icon: ico("yt.svg"),
      color: "#ff0000",
      type: "oauth_auto",
      fields: [],
      displayName: "YouTube",
    },
    {
      id: "linkedin",
      name: "LinkedIn",
      icon: ico("link.svg"),
      fields: [],
      type: "oauth_auto",
      color: "#0A66C2",
      displayName: "LinkedIn",
    },
  ];

  const [formData, setFormData] = useState({});

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await credentialsAPI.getMyCredentials(token);
      if (response.success) setCredentials(response.credentials);
    } catch (err) {
      console.error("Error fetching credentials:", err);
    } finally {
      setLoading(false);
    }
  };

  const isConnected = (platformId) =>
    credentials.some((cred) => cred.platform === platformId && cred.is_connected);

  const getCredential = (platformId) =>
    credentials.find((cred) => cred.platform === platformId);

  const handleInputChange = (platformId, fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [platformId]: { ...prev[platformId], [fieldName]: value },
    }));
  };

  // ✅ FACEBOOK OAuth (same logic)
  const handleFacebookAuthorizeAuto = async (platform) => {
    setConnectingPlatform(platform.id);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/oauth-auto/facebook/start`,
        { client_id: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data.success) {
        alert("Failed to start Facebook authorization");
        setConnectingPlatform(null);
        return;
      }

      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        response.data.authUrl,
        "Facebook Authorization",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        alert("Popup blocked! Please allow popups for this site.");
        setConnectingPlatform(null);
        return;
      }

      const messageHandler = (event) => {
        if (event.data.type === "facebook-oauth-success") {
          const message = event.data.instagramAccountId
            ? `✅ Connected: ${event.data.pageName}\n✅ Instagram also connected!`
            : `✅ Connected: ${event.data.pageName}`;

          window.removeEventListener("message", messageHandler);
          setConnectingPlatform(null);
          alert(message);
          fetchCredentials();
        } else if (event.data.type === "facebook-oauth-error") {
          window.removeEventListener("message", messageHandler);
          setConnectingPlatform(null);
          alert(`❌ Connection failed: ${event.data.error}`);
        }
      };

      window.addEventListener("message", messageHandler);

      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener("message", messageHandler);
          setConnectingPlatform(null);
        }
      }, 1000);
    } catch (error) {
      alert(`Failed to start authorization: ${error.message}`);
      setConnectingPlatform(null);
    }
  };

  // ✅ YOUTUBE OAuth (same logic)
  const handleYouTubeAuthorizeAuto = async (platform) => {
    setConnectingPlatform(platform.id);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/oauth-auto/youtube/start`,
        { client_id: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data.success) {
        alert("Failed to start YouTube authorization");
        setConnectingPlatform(null);
        return;
      }

      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        response.data.authUrl,
        "YouTube Authorization",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        alert("Popup blocked! Please allow popups for this site.");
        setConnectingPlatform(null);
        return;
      }

      const messageHandler = (event) => {
        if (event.data.type === "youtube-oauth-success") {
          window.removeEventListener("message", messageHandler);
          setConnectingPlatform(null);

          alert(`✅ Successfully connected: ${event.data.channelTitle}`);
          fetchCredentials();
        } else if (event.data.type === "youtube-oauth-error") {
          window.removeEventListener("message", messageHandler);
          setConnectingPlatform(null);
          alert(`❌ Connection failed: ${event.data.error}`);
        }
      };

      window.addEventListener("message", messageHandler);

      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener("message", messageHandler);
          setConnectingPlatform(null);
        }
      }, 1000);
    } catch (error) {
      alert("Failed to start authorization: " + error.message);
      setConnectingPlatform(null);
    }
  };

  // ✅ LINKEDIN OAuth (same logic)
  const handleLinkedInConnect = async () => {
    setConnectingPlatform("linkedin");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/oauth-auto/linkedin/start`,
        { client_id: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const popup = window.open(
          response.data.authUrl,
          "LinkedIn OAuth",
          `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!popup) {
          alert("Popup blocked! Please allow popups for this site.");
          setConnectingPlatform(null);
          return;
        }

        const messageHandler = (event) => {
          if (event.data.type === "linkedin-oauth-success") {
            window.removeEventListener("message", messageHandler);
            setConnectingPlatform(null);
            alert("✅ Successfully connected to LinkedIn");
            fetchCredentials();
          } else if (event.data.type === "linkedin-oauth-error") {
            window.removeEventListener("message", messageHandler);
            setConnectingPlatform(null);
            alert(`❌ Connection failed: ${event.data.error}`);
          }
        };

        window.addEventListener("message", messageHandler);

        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            window.removeEventListener("message", messageHandler);
            setConnectingPlatform(null);
          }
        }, 1000);
      }
    } catch (error) {
      alert("Failed to connect LinkedIn");
      setConnectingPlatform(null);
    }
  };

  // manual connect (kept same, even though you don’t use it now)
  const handleConnect = async (platform) => {
    const platformData = formData[platform.id] || {};
    const missingFields = platform.fields.filter((f) => !platformData[f.name]);
    if (missingFields.length > 0) {
      alert(`Please enter: ${missingFields.map((f) => f.label).join(", ")}`);
      return;
    }

    setConnectingPlatform(platform.id);

    try {
      const credentialData = {
        platform: platform.id,
        account_name: `${user.name}'s ${platform.name}`,
        client_id: null,
        ...platformData,
      };

      const verifyResult = await credentialsAPI.verifyCredential(credentialData, token);

      if (!verifyResult.success) {
        alert("❌ " + verifyResult.message);
        setConnectingPlatform(null);
        return;
      }

      const addResult = await credentialsAPI.addCredential(credentialData, token);

      if (addResult.success) {
        alert("✅ " + verifyResult.message);
        setFormData((prev) => ({ ...prev, [platform.id]: {} }));
        fetchCredentials();
      }
    } catch (err) {
      alert("Error: " + (err.message || "Failed to connect"));
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${platform.name}?`)) return;

    try {
      const credential = getCredential(platform.id);
      if (credential) {
        const result = await credentialsAPI.deleteCredential(credential.id, token);
        if (result.success) {
          alert(`${platform.name} disconnected successfully`);
          fetchCredentials();
        }
      }
    } catch (err) {
      alert("Error: " + (err.message || "Failed to disconnect"));
    }
  };

  return (
    <div className="qs-modal-overlay" onClick={onClose}>
      <div className="qs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qs-modal-header">
          <div className="qs-modal-title">
            <h2>Connect Accounts</h2>
            <p>Link your social platforms to start scheduling posts</p>
          </div>
          <button className="qs-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="qs-modal-body">
          {loading ? (
            <div className="qs-loading">Loading...</div>
          ) : (
            <div className="platform-grid">
              {platforms.map((platform) => {
                const connected = isConnected(platform.id);
                const credential = getCredential(platform.id);

                return (
                  <div
                    key={platform.id}
                    className={`platform-card ${connected ? "connected" : ""}`}
                  >
                    <div className="platform-header">
                      {platform.secondIcon ? (
                        <div className="platform-icons">
                          <img src={platform.secondIcon} alt="" />
                          <img src={platform.icon} alt="" />
                        </div>
                      ) : (
                        <div className="platform-icons single">
                          <img src={platform.icon} alt="" />
                          <span>{platform.displayName}</span>
                        </div>
                      )}

                      {connected && <span className="connected-badge">✓ Connected</span>}
                    </div>

                    {platform.secondIcon && !connected && <h4>{platform.displayName}</h4>}

                    {connected ? (
                      <div className="connected-info">
                        <p className="account-name">{credential?.account_name}</p>
                        <p className="connected-date">
                          Connected on{" "}
                          {credential?.created_at
                            ? new Date(credential.created_at).toLocaleDateString()
                            : "-"}
                        </p>
                        <button
                          className="btn-disconnect"
                          onClick={() => handleDisconnect(platform)}
                        >
                          Disconnect
                        </button>
                      </div>
                    ) : (
                      <div className="connect-form">
                        {platform.fields.map((field) => (
                          <input
                            key={field.name}
                            type={field.type || "text"}
                            placeholder={field.placeholder}
                            value={formData[platform.id]?.[field.name] || ""}
                            onChange={(e) =>
                              handleInputChange(platform.id, field.name, e.target.value)
                            }
                            className="credential-input"
                          />
                        ))}

                        <button
                          className="connect-btn"
                          onClick={() => {
                            if (platform.type === "oauth_auto") {
                              if (platform.id === "youtube") handleYouTubeAuthorizeAuto(platform);
                              else if (platform.id === "linkedin") handleLinkedInConnect();
                              else if (platform.id === "facebook")
                                handleFacebookAuthorizeAuto(platform);
                            } else {
                              handleConnect(platform);
                            }
                          }}
                          disabled={connectingPlatform === platform.id}
                        >
                          {connectingPlatform === platform.id ? "Connecting..." : "Connect"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CredentialsModal;
